"use client"

import { ComponentProps, createContext, useContext } from "react"
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import { cn } from "cn"

import IconCross from "@/components/icons/cross"
import { ButtonProps } from "./button"
import { IconButton } from "./icon-button"

type DrawerContextProps = {
  hasSnapPoints: boolean
  isNestedInDrawer: boolean
  modal: DrawerPrimitive.Root.Props["modal"]
  showSwipeHandle: boolean
  swipeDirection: NonNullable<DrawerPrimitive.Root.Props["swipeDirection"]>
}

const DrawerContext = createContext<DrawerContextProps | null>(null)

/** True for descendants of a Drawer — used to detect drawer-in-drawer nesting. */
const IsInDrawerContext = createContext(false)

function useDrawer() {
  const context = useContext(DrawerContext)

  if (!context) {
    throw new Error("useDrawer must be used within a Drawer.")
  }

  return context
}

function Drawer({
  modal = true,
  showSwipeHandle = true,
  snapPoints,
  swipeDirection = "down",
  ...props
}: DrawerPrimitive.Root.Props & {
  showSwipeHandle?: boolean
}) {
  const hasSnapPoints = snapPoints != null && snapPoints.length > 0
  const isNestedInDrawer = useContext(IsInDrawerContext)

  return (
    <IsInDrawerContext.Provider value={true}>
      <DrawerContext.Provider
        value={{
          hasSnapPoints,
          isNestedInDrawer,
          modal,
          showSwipeHandle,
          swipeDirection,
        }}
      >
        <DrawerPrimitive.Root
          data-slot="drawer"
          modal={modal}
          snapPoints={snapPoints}
          swipeDirection={swipeDirection}
          {...props}
        />
      </DrawerContext.Provider>
    </IsInDrawerContext.Provider>
  )
}

function DrawerTrigger({ ...props }: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({ ...props }: DrawerPrimitive.Portal.Props) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({ ...props }: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerCloseButton({
  variant = "ghost",
  className,
}: {
  variant?: ButtonProps["variant"]
  className?: string
}) {
  return (
    <DrawerClose
      render={
        <IconButton
          variant={variant}
          icon={{
            icon: IconCross,
            className: "text-[1.125em]",
            strokeWidth: 3,
          }}
          label="Close"
          className={cn("absolute top-2 left-2", className)}
        />
      }
    />
  )
}

function DrawerOverlay({
  className,
  forceRender,
  sheetEdge = false,
  ...props
}: DrawerPrimitive.Backdrop.Props & {
  /** Paints the bottom edge in the sheet's color on phones (see below). */
  sheetEdge?: boolean
}) {
  const { isNestedInDrawer } = useDrawer()
  // Base UI suppresses nested backdrops unless forceRender. Keep an overlay when
  // nested under a Sheet/Dialog, but not drawer-in-drawer (stacking handles that).
  const shouldForceRender = forceRender ?? !isNestedInDrawer

  return (
    <DrawerPrimitive.Backdrop
      data-slot="drawer-overlay"
      forceRender={shouldForceRender}
      // Fixed on iOS too, and split: Safari 26 tints the strip under the
      // status bar from the fixed layer at the top edge, but keeps a stale
      // color for a full-screen or empty dimming layer. The 12px child below
      // is neither, so it hands Safari the dim; the overlay starts under it.
      // No backdrop blur: it makes the child's `fixed` relative to the overlay.
      className={cn(
        "fixed inset-x-0 top-3 bottom-0 z-overlay min-h-dvh bg-black-a-7 opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))] transition-opacity duration-450 ease-drawer select-none data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-snap-points:[--drawer-overlay-min-opacity:0.5] data-starting-style:opacity-0 data-swiping:duration-0 dark:bg-black-a-9",
        className
      )}
      {...props}
    >
      <div
        aria-hidden
        className="fixed inset-x-0 top-0 h-3 bg-black-a-7 dark:bg-black-a-9"
      />
      {/* What Safari 26 samples at the bottom edge under a phone bottom
          sheet. It skips a fixed layer over 90% of the viewport's height, so
          a tall sheet alone leaves the strip under the toolbar the dim's gray.
          It has to live here: inside the sheet's transform it isn't fixed. */}
      {sheetEdge && (
        <div
          aria-hidden
          className="fixed inset-x-0 bottom-0 h-3 bg-background sm:hidden"
        />
      )}
    </DrawerPrimitive.Backdrop>
  )
}

function DrawerSwipeHandle({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-swipe-handle"
      aria-hidden="true"
      className={cn(
        "absolute inset-x-0 top-0 z-10 flex shrink-0 cursor-grab transition-opacity duration-200 group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-[swipe-axis=x]/drawer-popup:h-full group-data-[swipe-axis=x]/drawer-popup:w-2.5 group-data-[swipe-axis=x]/drawer-popup:items-center group-data-[swipe-axis=y]/drawer-popup:h-2.5 group-data-[swipe-axis=y]/drawer-popup:w-full group-data-[swipe-axis=y]/drawer-popup:justify-center group-data-[swipe-direction=down]/drawer-popup:items-end group-data-[swipe-direction=left]/drawer-popup:order-last group-data-[swipe-direction=left]/drawer-popup:justify-start group-data-[swipe-direction=right]/drawer-popup:justify-end group-data-[swipe-direction=up]/drawer-popup:order-last group-data-[swipe-direction=up]/drawer-popup:items-start after:block after:shrink-0 after:rounded-full after:bg-current/10 group-data-[swipe-axis=x]/drawer-popup:after:h-12 group-data-[swipe-axis=x]/drawer-popup:after:w-1.25 group-data-[swipe-axis=y]/drawer-popup:after:h-1.25 group-data-[swipe-axis=y]/drawer-popup:after:w-12 active:cursor-grabbing",
        className
      )}
      {...props}
    />
  )
}

function DrawerContent({
  className,
  children,
  size = "default",
  overlay = true,
  overlayClassName,
  viewportClassName,
  portalContainer,
  shape = "drawer",
  stack = true,
  ...props
}: DrawerPrimitive.Popup.Props & {
  size?: "default" | "full"
  overlay?: boolean
  overlayClassName?: string
  viewportClassName?: string
  portalContainer?: HTMLElement | null
  /** `"none"` drops the inset card look so the caller can supply its own. */
  shape?: "drawer" | "none"
  /** Whether the popup recedes when a nested drawer opens over it. */
  stack?: boolean
}) {
  const { hasSnapPoints, modal, showSwipeHandle, swipeDirection } = useDrawer()
  const swipeAxis =
    swipeDirection === "down" || swipeDirection === "up" ? "y" : "x"
  const keyboardAware = swipeAxis === "y"

  const portal = (
    <DrawerPortal data-slot="drawer-portal" container={portalContainer}>
      {modal === true && overlay && (
        <DrawerOverlay
          className={overlayClassName}
          data-snap-points={hasSnapPoints ? "" : undefined}
          sheetEdge={shape === "drawer" && swipeDirection === "down"}
        />
      )}
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        data-modal={modal}
        // Starts below the overlay's top strip so Safari 26 samples the dim
        // there, not this empty full-screen layer. The popup is fixed itself,
        // so this doesn't move it.
        className={cn(
          "pointer-events-none fixed inset-x-0 top-3 bottom-0 z-overlay select-none data-[modal=true]:pointer-events-auto",
          viewportClassName
        )}
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-popup"
          data-swipe-axis={swipeAxis}
          data-snap-points={hasSnapPoints ? "" : undefined}
          className={cn(
            /** Positioning and swipe/stack transforms that Base UI drives through CSS vars. */
            // Base.
            "group/drawer-popup pointer-events-auto fixed z-overlay flex min-h-0 transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] flex-col transition-[transform,height,opacity,filter] duration-450 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform outline-none select-none [interpolate-size:allow-keywords]",
            // Stack.
            "[--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))] [--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--stack-shrink:calc(1-var(--stack-scale))]",
            // Transitions.
            "data-ending-style:transform-(--closed-transform) data-ending-style:opacity-[0.9999] data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-nested-drawer-swiping:duration-0 data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-starting-style:transform-(--closed-transform) data-swiping:duration-0 data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)]",
            // Axis: y.
            "data-[swipe-axis=y]:inset-x-0",
            // Axis: x.
            "data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:flex-row",
            // Direction: down.
            "data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)] data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]",
            // Direction: up.
            "data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)] data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]",
            // Direction: left.
            "data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)] data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]",
            // Direction: right.
            "data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)] data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]",
            /** How the popup recedes behind a nested drawer. */
            stack &&
              "[--drawer-stacked-shadow:0_-20px_25px_-5px_rgb(0_0_0/0.1),0_-8px_10px_-6px_rgb(0_0_0/0.1)] [--peek:1rem] [--stack-step:0.05] data-nested-drawer-open:overflow-hidden data-nested-drawer-open:brightness-95 data-[swipe-axis=y]:data-nested-drawer-open:h-(--stack-height) data-[swipe-direction=down]:data-nested-drawer-open:shadow-(--drawer-stacked-shadow)",
            /** Zeroes the stack offsets so the popup stays put under a nested drawer. */
            !stack && "[--peek:0px] [--stack-step:0]",
            /** The inset, rounded card look. */
            shape === "drawer" && [
              // Base.
              "m-(--drawer-inset,0px) h-(--drawer-content-height) max-h-(--drawer-content-max-height,none) w-(--drawer-content-width,auto) rounded-[min(var(--radius-4xl),24px)] bg-background text-sm text-foreground shadow-ring-xl [--drawer-bleed-background:transparent] [--drawer-inset:--spacing(2)]",
              // Phones: a bottom sheet runs flush and full width, so Safari 26
              // paints the strip under its toolbar in the sheet's color (see
              // the overlay's edge strip).
              "max-sm:data-[swipe-direction=down]:rounded-b-none max-sm:data-[swipe-direction=down]:pb-[env(safe-area-inset-bottom,0px)] max-sm:data-[swipe-direction=down]:[--drawer-inset:0px]",
              // Bleed.
              "[--bleed:3rem] after:pointer-events-none after:absolute after:bg-(--drawer-bleed-background,var(--color-background)) data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-(--bleed) data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-(--bleed) data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full",
              // Sizing.
              "[--drawer-content-height:var(--drawer-height,auto)] [--drawer-top-gap:2.5rem] data-[swipe-axis=x]:[--drawer-content-width:75%] data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-var(--drawer-top-gap))] data-[swipe-axis=y]:data-snap-points:[--drawer-content-height:100dvh]",
            ],
            shape === "drawer" &&
              size === "full" &&
              "data-[swipe-axis=y]:[--drawer-content-height:calc(100dvh-var(--drawer-top-gap))]",
            className
          )}
          {...props}
        >
          {showSwipeHandle && <DrawerSwipeHandle />}
          <DrawerPrimitive.Content
            data-slot="drawer-content"
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain rounded-[inherit] transition-opacity duration-300 ease-[cubic-bezier(0.45,1.005,0,1.005)] select-text group-data-swiping/drawer-popup:select-none",
              stack &&
                "group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100"
            )}
          >
            {/* A popup that doesn't stack can't host a nested drawer's
                backdrop, so nested drawers render their own. */}
            <IsInDrawerContext.Provider value={stack}>
              {children}
            </IsInDrawerContext.Provider>
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  )

  // Bottom and top sheets with form fields. Side drawers are unaffected, and
  // the provider no-ops when nothing inside can take the software keyboard.
  if (keyboardAware) {
    return (
      <DrawerPrimitive.VirtualKeyboardProvider>
        {portal}
      </DrawerPrimitive.VirtualKeyboardProvider>
    )
  }

  return portal
}

function DrawerHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex shrink-0 flex-col gap-0.5 p-(--dialog-pad) pt-7 group-data-[swipe-axis=y]/drawer-popup:text-center",
        className
      )}
      {...props}
    />
  )
}

function DrawerBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        "flex min-h-0 flex-1 scroll-fade flex-col overflow-x-hidden overflow-y-auto overscroll-contain where:p-(--dialog-pad) where:pt-12 last:where:pb-12 [:where([data-slot=drawer-header]+&)]:pt-4",
        className
      )}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        "mt-auto flex shrink-0 flex-col gap-2 p-(--dialog-pad)",
        // A footer that holds the field (the comment composer) sits above the
        // software keyboard. Bare var() is invalid before the provider aligns.
        "has-[input,textarea]:pb-[calc(var(--dialog-pad)+var(--drawer-keyboard-inset,0px))]",
        className
      )}
      {...props}
    />
  )
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("text-md font-semibold", className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-sm text-balance text-muted", className)}
      {...props}
    />
  )
}

export type DrawerHandle = DrawerPrimitive.Root.Props["handle"]

export type DrawerContentProps = ComponentProps<typeof DrawerContent>

const createDrawerHandle = DrawerPrimitive.createHandle

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerSwipeHandle,
  DrawerTrigger,
  DrawerClose,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  createDrawerHandle,
}
